import { useAppStore } from '../store/appStore';
import type { BudgetData, ImportResult } from '../types';
import {
  isEncryptedPayload,
  decryptData,
  type EncryptedPayload,
} from './encryptionService';

export const DATA_VERSION = '1.0';
const SUPPORTED_VERSIONS = ['1.0'];

export function serializeBudgetData(): BudgetData {
  const state = useAppStore.getState();
  return {
    version: DATA_VERSION,
    exportedAt: new Date().toISOString(),
    transactions: state.transactions,
    categories: state.categories,
    budgets: state.budgets,
    corrections: state.corrections,
  };
}

export function exportData(): void {
  const data = serializeBudgetData();

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `budgeter-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

function validateBudgetData(data: unknown): data is BudgetData {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;

  if (typeof obj.version !== 'string') return false;
  if (typeof obj.exportedAt !== 'string') return false;
  if (!Array.isArray(obj.transactions)) return false;
  if (!Array.isArray(obj.categories)) return false;
  if (!Array.isArray(obj.budgets)) return false;
  if (!Array.isArray(obj.corrections)) return false;

  return true;
}

export interface EncryptedImportResult {
  status: 'encrypted';
  encryptedPayload: EncryptedPayload;
  file: File;
}

export type CheckImportResult = ImportResult | EncryptedImportResult;

function validateParsedData(
  data: unknown
): ImportResult | { status: 'valid'; data: BudgetData } {
  if (!validateBudgetData(data)) {
    return { status: 'error', error: 'Estructura de archivo inválida' };
  }

  if (!SUPPORTED_VERSIONS.includes(data.version)) {
    return {
      status: 'error',
      error: `Versión no soportada: ${data.version}`,
    };
  }

  return { status: 'valid' as const, data };
}

function checkConflictsForData(data: BudgetData): ImportResult {
  const state = useAppStore.getState();
  const existingCounts = {
    transactions: state.transactions.length,
    categories: state.categories.length,
    budgets: state.budgets.length,
    corrections: state.corrections.length,
  };

  const hasExistingData =
    existingCounts.transactions > 0 ||
    existingCounts.categories > 0 ||
    existingCounts.budgets > 0 ||
    existingCounts.corrections > 0;

  const imported = {
    transactions: data.transactions.length,
    categories: data.categories.length,
    budgets: data.budgets.length,
    corrections: data.corrections.length,
  };

  if (hasExistingData) {
    return { status: 'conflict', imported, existingCounts };
  }

  return { status: 'success', imported };
}

export async function checkImportConflicts(
  file: File
): Promise<CheckImportResult> {
  try {
    const text = await file.text();
    let data: unknown;
    try {
      data = JSON.parse(text);
    } catch {
      return { status: 'error', error: 'Archivo JSON inválido' };
    }

    if (isEncryptedPayload(data)) {
      return { status: 'encrypted', encryptedPayload: data, file };
    }

    const validation = validateParsedData(data);
    if (validation.status !== 'valid') return validation;

    return checkConflictsForData(validation.data);
  } catch {
    return { status: 'error', error: 'Error al leer el archivo' };
  }
}

export async function decryptAndCheckImport(
  encryptedPayload: EncryptedPayload,
  password: string,
  _file: File
): Promise<CheckImportResult> {
  try {
    const decryptedText = await decryptData(encryptedPayload, password);

    let data: unknown;
    try {
      data = JSON.parse(decryptedText);
    } catch {
      return { status: 'error', error: 'Datos descifrados inválidos' };
    }

    const validation = validateParsedData(data);
    if (validation.status !== 'valid') return validation;

    const result = checkConflictsForData(validation.data);
    return {
      ...result,
      decryptedData: validation.data,
    } as ImportResult & { decryptedData: BudgetData };
  } catch {
    return { status: 'error', error: 'Contraseña incorrecta' };
  }
}

export type ImportMode = 'replace' | 'merge';

async function importBudgetData(
  data: BudgetData,
  mode: ImportMode
): Promise<ImportResult> {
  try {
    let store = useAppStore.getState();

    if (mode === 'replace') {
      await store.clearAllData();
      store = useAppStore.getState();
    }

    const existingTransactionIds = new Set(store.transactions.map((t) => t.id));
    const existingCategoryIds = new Set(store.categories.map((c) => c.id));
    const existingBudgetIds = new Set(store.budgets.map((b) => b.id));
    const existingCorrectionIds = new Set(store.corrections.map((c) => c.id));

    const newTransactions = data.transactions.filter(
      (t) => !existingTransactionIds.has(t.id)
    );
    const newCategories = data.categories.filter(
      (c) => !existingCategoryIds.has(c.id)
    );
    const newBudgets = data.budgets.filter((b) => !existingBudgetIds.has(b.id));
    const newCorrections = data.corrections.filter(
      (c) => !existingCorrectionIds.has(c.id)
    );

    if (newCategories.length > 0) {
      for (const category of newCategories) {
        store.addCategory(category);
      }
    }

    if (newTransactions.length > 0) {
      store.addTransactions(newTransactions);
    }

    for (const budget of newBudgets) {
      store.setBudget(budget);
    }

    for (const correction of newCorrections) {
      store.addCorrection(correction);
    }

    return {
      status: 'success',
      imported: {
        transactions: newTransactions.length,
        categories: newCategories.length,
        budgets: newBudgets.length,
        corrections: newCorrections.length,
      },
    };
  } catch {
    return { status: 'error', error: 'Error al procesar la importación' };
  }
}

export async function importData(
  file: File,
  mode: ImportMode,
  decryptedData?: BudgetData
): Promise<ImportResult> {
  if (decryptedData) {
    return importBudgetData(decryptedData, mode);
  }

  try {
    const text = await file.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { status: 'error', error: 'Archivo JSON inválido' };
    }

    const validation = validateParsedData(parsed);
    if (validation.status !== 'valid') return validation;

    return importBudgetData(validation.data, mode);
  } catch {
    return { status: 'error', error: 'Error al procesar la importación' };
  }
}

export function checkImportConflictsFromString(
  jsonString: string
): ImportResult {
  let data: unknown;
  try {
    data = JSON.parse(jsonString);
  } catch {
    return { status: 'error', error: 'Archivo JSON inválido' };
  }

  const validation = validateParsedData(data);
  if (validation.status !== 'valid') return validation;

  return checkConflictsForData(validation.data);
}

export async function importDataFromString(
  jsonString: string,
  mode: ImportMode
): Promise<ImportResult> {
  let data: unknown;
  try {
    data = JSON.parse(jsonString);
  } catch {
    return { status: 'error', error: 'Archivo JSON inválido' };
  }

  const validation = validateParsedData(data);
  if (validation.status !== 'valid') return validation;

  return importBudgetData(validation.data, mode);
}
